import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ContractorForm from '../../components/ContractorForm';
import { rate } from '../../lib/money';
import { useSettings } from './SettingsContext';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

const AVAIL_WORD = { available: 'Available', busy: 'Busy', unavailable: 'Unavailable', 'on-leave': 'On leave', inactive: 'Inactive' };

/**
 * Settings -> People & access -> Contractors.
 *
 * The one contractor form (components/ContractorForm.js, shared with Ticket
 * Mgmt -> Contractors), the list, and what each contractor may do in their
 * portal beyond their own jobs:
 *
 *   May manage ticket templates (088) - Gordon, 26 October: "contractors can
 *   access ticket templates in their portal if they have permissions to edit
 *   it". Off for everybody until ticked; on, the portal gets a Templates tab
 *   where they see and edit the firm's templates, as Ticket Mgmt does. The
 *   server checks it on every template request.
 *
 * Deactivating asks in the card rather than in a window.confirm().
 */
export default function ContractorsPage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [asking, setAsking] = useState(null);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contractors`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setProblem(errorOf(e, 'Could not load the contractors.'));
      setRows((x) => x || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Before 088 the server says so (GET /app-settings setup), and the list
  // carries no may_manage_templates at all.
  const { rules } = useSettings();
  const templatesReady = rules?.setup?.m088_settings === false ? false
    : ((rows || []).some((c) => 'may_manage_templates' in c) || (rows || []).length === 0);

  const submit = async (values) => {
    try {
      if (editing) await axios.put(`${API}/contractors/${editing.id}`, values);
      else await axios.post(`${API}/contractors`, values);
    } catch (err) {
      throw new Error(errorOf(err, 'Failed to save contractor'));
    }
    setDone(`${values.name} ${editing ? 'saved' : 'added'}.`);
    setProblem(null);
    setEditing(null);
    await load();
  };

  const deactivate = async (c) => {
    setAsking(null);
    try {
      await axios.delete(`${API}/contractors/${c.id}`);
      setDone(`${c.name} is deactivated. Their portal stops working at once.`);
      await load();
    } catch (err) { setProblem(errorOf(err, 'Failed to deactivate contractor')); }
  };

  const setTemplates = async (c, value) => {
    setBusyId(c.id);
    setProblem(null);
    try {
      await axios.put(`${API}/contractors/${c.id}/ticket-templates`, { may_manage: value });
      setRows((list) => list.map((x) => (x.id === c.id ? { ...x, may_manage_templates: value } : x)));
      setDone(value
        ? `${c.name} can now see and edit ticket templates from their portal.`
        : `${c.name} can no longer see ticket templates.`);
    } catch (err) {
      setProblem(errorOf(err, 'Could not change that.'));
    } finally { setBusyId(null); }
  };

  return (
    <div className="stn-contractors">
      {problem && (
        <div className="stn-banner is-problem" role="alert">
          <span>{problem}</span>
          <button type="button" className="btn btn-small btn-secondary" onClick={() => setProblem(null)}>Dismiss</button>
        </div>
      )}
      <p className="stn-flash" role="status" aria-live="polite">{done || ''}</p>

      <div id="s-form">
        <ContractorForm
          initial={editing}
          title={editing ? `Edit ${editing.name}` : 'Add a contractor'}
          onSubmit={submit}
          onCancel={editing ? () => setEditing(null) : null}
          submitLabel={editing ? 'Save changes' : 'Add contractor'}
        />
      </div>

      <section id="s-list" aria-labelledby="stn-con-h">
        <div className="stn-section-head">
          <h3 className="stn-card-title" id="stn-con-h">Contractors</h3>
          <p className="stn-help">
            Contractors sign in at /login with a code emailed to them; there is nothing to hand out.
            Rates, billing and their tickets are under <Link to="/manage/contractors">Ticket Mgmt → Contractors</Link>.
          </p>
        </div>
        {!templatesReady && (
          <p className="stn-banner is-warn" role="status">
            Letting contractors manage ticket templates needs migration 088 — run npm run migrate:088.
          </p>
        )}
        <ul className="stn-grid stn-grid-wide">
          {rows === null && <li className="stn-quiet">Loading…</li>}
          {rows && rows.length === 0 && <li className="stn-quiet">No contractors yet.</li>}
          {(rows || []).map((c) => {
            const off = c.availability_status === 'inactive';
            return (
              <li key={c.id} className={`stn-item${off ? ' is-off' : ''}`} data-contractor={c.email}>
                <div className="stn-item-head">
                  <h4 className="stn-item-name">{c.name}</h4>
                  <span className={`stn-pill${c.availability_status === 'available' ? ' is-good' : ''}`}>
                    {AVAIL_WORD[c.availability_status] || 'Available'}
                  </span>
                </div>
                <dl className="stn-facts">
                  <div><dt>Email</dt><dd>{c.email}</dd></div>
                  {c.phone && <div><dt>Phone</dt><dd>{c.phone}</dd></div>}
                  {c.company && <div><dt>Company</dt><dd>{c.company}</dd></div>}
                  {c.skills && <div><dt>Skills</dt><dd>{c.skills}</dd></div>}
                  {/* What the register says they hold (093): a link onto the
                      register filtered to them, for IT to see or take back. */}
                  {c.held_count > 0 && (
                    <div><dt>Devices</dt><dd>
                      <Link to={`/assets?holder=c:${c.id}`}>{`Holds ${c.held_count} device${c.held_count === 1 ? '' : 's'}`}</Link>
                    </dd></div>
                  )}
                  {c.hourly_rate ? <div><dt>Rate</dt><dd>{rate(c.hourly_rate)}</dd></div> : null}
                </dl>
                {templatesReady && (
                  <label className="stn-perm">
                    <input type="checkbox" checked={c.may_manage_templates === true}
                           disabled={off || busyId === c.id}
                           onChange={(e) => setTemplates(c, e.target.checked)} />
                    <span>
                      <strong>May manage ticket templates</strong>
                      <small>
                        {c.may_manage_templates
                          ? 'On: a Templates tab in their portal, where they see and edit the firm’s templates.'
                          : 'Off: they never see the templates.'}
                      </small>
                    </span>
                  </label>
                )}
                {asking === c.id ? (
                  <div className="stn-confirm" role="group" aria-label={`Deactivate ${c.name}?`}>
                    <p>Deactivate {c.name}? Their portal stops working at once. Their tickets, hours and invoices are kept.</p>
                    <div className="stn-confirm-actions">
                      <button type="button" className="btn btn-small btn-danger" onClick={() => deactivate(c)}>Deactivate</button>
                      <button type="button" className="btn btn-small btn-secondary" onClick={() => setAsking(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="stn-row-actions">
                    <button type="button" className="btn btn-small btn-secondary" aria-label={`Edit ${c.name}`}
                            onClick={() => {
                              setEditing(c);
                              const el = document.getElementById('s-form');
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}>Edit</button>
                    {!off && (
                      <button type="button" className="btn btn-small btn-danger-outline" onClick={() => setAsking(c.id)}>
                        Deactivate
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
